<template>
    <div class="tw-grid tw-grid-cols-12 tw-gap-4">
        <div class="tw-col-span-12 lg:tw-col-span-12">
            <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200 tw-min-h-[600px]">
                <!-- Header Section -->
                <div class="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-4 tw-mb-6">
                    <div>
                        <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-1">Services</h1>
                        <p class="tw-text-sm tw-text-gray-600">{{ totalItems }} services total</p>
                    </div>
                    
                    <!-- Header Actions -->
                    <div class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                        <!-- Search -->
                        <div class="tw-min-w-[250px]">
                            <FormField
                                v-model="searchQuery"
                                type="text"
                                placeholder="Search services..."
                                left-icon="solar:magnifer-linear"
                                hide-label
                                hide-errors
                                :disabled="loading"
                                class="tw-w-full"
                            />
                        </div>
                        
                        <!-- View Toggle -->
                        <div class="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-p-1">
                            <button
                                @click="viewMode = 'grid'"
                                :disabled="loading"
                                :class="[
                                    'tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-transition-colors',
                                    viewMode === 'grid' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-text-gray-600 hover:tw-text-gray-900',
                                    loading && 'tw-opacity-50 tw-cursor-not-allowed'
                                ]"
                            >
                                <icon icon="solar:widget-4-bold-duotone" class="tw-text-lg" />
                            </button>
                            <button
                                @click="viewMode = 'list'"
                                :disabled="loading"
                                :class="[
                                    'tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-transition-colors',
                                    viewMode === 'list' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-text-gray-600 hover:tw-text-gray-900',
                                    loading && 'tw-opacity-50 tw-cursor-not-allowed'
                                ]"
                            >
                                <icon icon="solar:list-bold-duotone" class="tw-text-lg" />
                            </button>
                        </div>
                        
                        <!-- Add Service Button -->
                        <a-button
                            as="router-link"
                            :to="{ name: 'pages.company.services.create' }"
                            intent="gradient"
                            left-icon="solar:add-circle-bold-duotone"
                            :disabled="loading"
                        >
                            Add Service
                        </a-button>
                    </div>
                </div>

                <!-- Filters Bar -->
                <div v-if="false" class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-mb-6 tw-p-4 tw-bg-gray-50 tw-rounded-lg">
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <!-- Status Filter -->
                        <div class="tw-w-48">
                            <FormField
                                v-model="filters.status"
                                type="select"
                                hide-label
                                hide-errors
                                left-icon="solar:settings-linear"
                                @change="onFilterChange"
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active Only</option>
                                <option value="inactive">Inactive Only</option>
                            </FormField>
                        </div>

                        <!-- Clear Filters -->
                        <a-button
                            v-if="searchQuery || filters.status !== 'all'"
                            @click="clearFilters"
                            intent="light"
                            left-icon="solar:close-circle-linear"
                            :disabled="loading"
                        >
                            Clear Filters
                        </a-button>
                    </div>

                    <!-- Filter Summary -->
                    <div class="tw-text-sm tw-text-gray-600">
                        <span>{{ totalItems }} service{{ totalItems !== 1 ? 's' : '' }} found</span>
                        <span v-if="filters.status !== 'all'" class="tw-ml-2">
                            •
                            <span
                                :class="[
                                    'tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-ml-1',
                                    filters.status === 'active' 
                                        ? 'tw-bg-green-100 tw-text-green-800' 
                                        : 'tw-bg-red-100 tw-text-red-800'
                                ]"
                            >
                                {{ filters.status }}
                            </span>
                        </span>
                    </div>
                </div>

                <!-- Content Area -->
                <div class="tw-flex-1">
                    <!-- Loading State -->
                    <div v-if="loading">
                        <!-- Grid View Skeleton -->
                        <div v-if="viewMode === 'grid'" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 xl:tw-grid-cols-4 tw-gap-4">
                            <div
                                v-for="n in 8"
                                :key="n"
                                class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-animate-pulse"
                            >
                                <!-- Skeleton Checkbox and Actions -->
                                <div class="tw-flex tw-items-start tw-justify-between tw-mb-3">
                                    <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-flex tw-items-center tw-gap-1">
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                    </div>
                                </div>

                                <!-- Skeleton Icon -->
                                <div class="tw-w-full tw-h-32 tw-bg-gray-200 tw-rounded-lg tw-mb-3"></div>

                                <!-- Skeleton Service Info -->
                                <div>
                                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2 tw-w-3/4"></div>
                                    <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-mb-2 tw-w-full"></div>
                                    <div class="tw-flex tw-gap-1 tw-mt-2">
                                        <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-16"></div>
                                        <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-12"></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- List View Skeleton -->
                        <div v-else class="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                            <table class="tw-min-w-full tw-divide-y tw-divide-gray-200">
                                <thead class="tw-bg-gray-50">
                                    <tr>
                                        <th class="tw-px-4 tw-py-3 tw-text-left">
                                            <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                        </th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Image</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Name</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Description</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Categories</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Created</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
                                    <tr
                                        v-for="n in 12"
                                        :key="n"
                                        class="tw-animate-pulse"
                                    >
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-w-16 tw-h-16 tw-bg-gray-200 tw-rounded-lg"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-48"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-flex tw-gap-1">
                                                <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-16"></div>
                                                <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-12"></div>
                                            </div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-20"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4 tw-text-right">
                                            <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
                                                <div class="tw-w-6 tw-h-6 tw-bg-gray-200 tw-rounded"></div>
                                                <div class="tw-w-6 tw-h-6 tw-bg-gray-200 tw-rounded"></div>
                                                <div class="tw-w-6 tw-h-6 tw-bg-gray-200 tw-rounded"></div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Empty State -->
                    <div v-else-if="services.length === 0 && !searchQuery" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center">
                        <icon icon="solar:settings-linear" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">No services yet</h3>
                        <p class="tw-text-gray-600 tw-mb-4">Start by adding your first service</p>
                        <a-button
                            as="router-link"
                            :to="{ name: 'pages.company.services.create' }"
                            intent="primary"
                            left-icon="solar:add-circle-bold-duotone"
                        >
                            Add Service
                        </a-button>
                    </div>

                    <!-- No Search Results -->
                    <div v-else-if="services.length === 0" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center">
                        <icon icon="solar:settings-bold-duotone" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">No services found</h3>
                        <p class="tw-text-gray-600 tw-mb-4">Try adjusting your search or filters</p>
                        <a-button
                            @click="clearFilters"
                            intent="secondary"
                            left-icon="solar:refresh-bold-duotone"
                        >
                            Clear Filters
                        </a-button>
                    </div>

                    <!-- Grid View -->
                    <div v-else-if="viewMode === 'grid'" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 xl:tw-grid-cols-4 tw-gap-4">
                        <div
                            v-for="service in services"
                            :key="service.id"
                            class="tw-group tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 hover:tw-shadow-md tw-transition-all tw-duration-200 tw-cursor-pointer"
                            @click="selectService(service)"
                        >
                            <!-- Selection Checkbox -->
                            <div class="tw-flex tw-items-start tw-justify-between tw-mb-3">
                                <input
                                    type="checkbox"
                                    :checked="selectedServices.includes(service.id)"
                                    @click.stop="toggleServiceSelection(service.id)"
                                    class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                />
                                <div class="tw-flex tw-items-center tw-gap-1 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
                                    <button
                                        @click.stop="viewService(service)"
                                        class="tw-p-1.5 tw-text-gray-400 hover:tw-text-gray-600 tw-rounded-md hover:tw-bg-gray-100"
                                        title="View"
                                    >
                                        <icon icon="solar:eye-bold-duotone" class="tw-text-lg" />
                                    </button>
                                    <a-button
                                        as="router-link"
                                        :to="{ name: 'pages.company.services.edit', params: { id: service.id } }"
                                        @click.stop
                                        intent="ghost"
                                        class="tw-p-1.5 tw-text-gray-400 hover:tw-text-gray-600 tw-rounded-md hover:tw-bg-gray-100"
                                        title="Edit"
                                    >
                                        <icon icon="solar:pen-bold-duotone" class="tw-text-lg" />
                                    </a-button>
                                    <button
                                        @click.stop="deleteService(service)"
                                        class="tw-p-1.5 tw-text-gray-400 hover:tw-text-red-600 tw-rounded-md hover:tw-bg-gray-100"
                                        title="Delete"
                                    >
                                        <icon icon="solar:trash-bin-2-bold-duotone" class="tw-text-lg" />
                                    </button>
                                </div>
                            </div>

                            <!-- Service Image -->
                            <div class="tw-flex tw-items-center tw-justify-center tw-h-32 tw-bg-gray-50 tw-rounded-lg tw-mb-3">
                                <img
                                    v-if="service.image"
                                    :src="service.image"
                                    :alt="service.name"
                                    class="tw-max-w-full tw-max-h-full tw-object-cover tw-rounded"
                                />
                                <icon
                                    v-else
                                    icon="solar:settings-bold-duotone"
                                    class="tw-text-4xl tw-text-gray-400"
                                />
                            </div>

                            <!-- Service Info -->
                            <div>
                                <h4 class="tw-font-medium tw-text-gray-900 tw-truncate tw-mb-1" :title="service.name">{{ service.name }}</h4>
                                <p class="tw-text-sm tw-text-gray-600 tw-line-clamp-2 tw-mb-2">{{ service.description }}</p>
                                <div v-if="service.categories && service.categories.length > 0" class="tw-flex tw-items-center tw-gap-1 tw-mt-2 tw-overflow-hidden">
                                    <span
                                        v-for="category in service.categories.slice(0, 2)"
                                        :key="category.id"
                                        class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium tw-bg-primary-100 tw-text-primary-800 tw-flex-shrink-0 tw-max-w-[80px]"
                                        :title="category.name"
                                    >
                                        <span class="tw-truncate">{{ category.name }}</span>
                                    </span>
                                    <span
                                        v-if="service.categories.length > 2"
                                        class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium tw-bg-gray-100 tw-text-gray-800 tw-flex-shrink-0"
                                    >
                                        +{{ service.categories.length - 2 }}
                                    </span>
                                </div>
                                <div class="tw-flex tw-items-center tw-justify-between tw-mt-2">
                                    <span
                                        :class="[
                                            'tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium',
                                            service.status === 'active' 
                                                ? 'tw-bg-green-100 tw-text-green-800' 
                                                : 'tw-bg-red-100 tw-text-red-800'
                                        ]"
                                    >
                                        <icon 
                                            :icon="service.status === 'active' ? 'solar:check-circle-bold' : 'solar:close-circle-bold'" 
                                            class="tw-mr-1 tw-text-xs" 
                                        />
                                        {{ service.status === 'active' ? 'Active' : 'Inactive' }}
                                    </span>
                                    <div v-if="service.created_at" class="tw-text-xs tw-text-gray-500">{{ formatDate(service.created_at) }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- List View -->
                    <div v-else class="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                        <table class="tw-min-w-full tw-divide-y tw-divide-gray-200">
                            <thead class="tw-bg-gray-50">
                                <tr>
                                    <th class="tw-px-4 tw-py-3 tw-text-left">
                                        <input
                                            type="checkbox"
                                            :checked="allServicesSelected"
                                            @change="toggleAllServices"
                                            class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                        />
                                    </th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Icon</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Name</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Description</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Categories</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Status</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Created</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
                                <tr
                                    v-for="service in services"
                                    :key="service.id"
                                    class="hover:tw-bg-gray-50"
                                >
                                    <td class="tw-px-4 tw-py-4">
                                        <input
                                            type="checkbox"
                                            :checked="selectedServices.includes(service.id)"
                                            @change="toggleServiceSelection(service.id)"
                                            class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                        />
                                    </td>
                                    <td class="tw-px-4 tw-py-4">
                                        <div class="tw-w-16 tw-h-16 tw-bg-gray-50 tw-rounded-lg tw-flex tw-items-center tw-justify-center">
                                            <img
                                                v-if="service.image"
                                                :src="service.image"
                                                :alt="service.name"
                                                class="tw-w-full tw-h-full tw-object-cover tw-rounded-lg"
                                            />
                                            <icon
                                                v-else
                                                icon="solar:settings-bold-duotone"
                                                class="tw-text-2xl tw-text-gray-400"
                                            />
                                        </div>
                                    </td>
                                    <td class="tw-px-4 tw-py-4">
                                        <div class="tw-text-sm tw-font-medium tw-text-gray-900">{{ service.name }}</div>
                                    </td>
                                    <td class="tw-px-4 tw-py-4">
                                        <div class="tw-text-sm tw-text-gray-500 tw-max-w-xs tw-truncate">{{ service.description }}</div>
                                    </td>
                                    <td class="tw-px-4 tw-py-4 tw-w-48">
                                        <div v-if="service.categories && service.categories.length > 0" class="tw-flex tw-items-center tw-gap-1 tw-overflow-hidden">
                                            <span
                                                v-for="category in service.categories.slice(0, 2)"
                                                :key="category.id"
                                                class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium tw-bg-primary-100 tw-text-primary-800 tw-flex-shrink-0 tw-max-w-[90px]"
                                                :title="category.name"
                                            >
                                                <span class="tw-truncate">{{ category.name }}</span>
                                            </span>
                                            <span
                                                v-if="service.categories.length > 2"
                                                class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium tw-bg-gray-100 tw-text-gray-800 tw-flex-shrink-0"
                                            >
                                                +{{ service.categories.length - 2 }}
                                            </span>
                                        </div>
                                        <span v-else class="tw-text-xs tw-text-gray-400">No categories</span>
                                    </td>
                                    <td class="tw-px-4 tw-py-4">
                                        <span
                                            :class="[
                                                'tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium',
                                                service.status === 'active' 
                                                    ? 'tw-bg-green-100 tw-text-green-800' 
                                                    : 'tw-bg-red-100 tw-text-red-800'
                                            ]"
                                        >
                                            <icon 
                                                :icon="service.status === 'active' ? 'solar:check-circle-bold' : 'solar:close-circle-bold'" 
                                                class="tw-mr-1 tw-text-xs" 
                                            />
                                            {{ service.status === 'active' ? 'Active' : 'Inactive' }}
                                        </span>
                                    </td>
                                    <td class="tw-px-4 tw-py-4 tw-text-sm tw-text-gray-500">
                                        <span v-if="service.created_at">{{ formatDate(service.created_at) }}</span>
                                        <span v-else class="tw-text-gray-400">ID: {{ service.id }}</span>
                                    </td>
                                    <td class="tw-px-4 tw-py-4 tw-text-right tw-text-sm tw-font-medium">
                                        <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
                                            <button
                                                @click="viewService(service)"
                                                class="tw-text-gray-400 hover:tw-text-gray-600"
                                                title="View"
                                            >
                                                <icon icon="solar:eye-bold-duotone" class="tw-text-lg" />
                                            </button>
                                            <a-button
                                                as="router-link"
                                                :to="{ name: 'pages.company.services.edit', params: { id: service.id } }"
                                                intent="ghost"
                                                class="tw-text-gray-400 hover:tw-text-gray-600"
                                                title="Edit"
                                            >
                                                <icon icon="solar:pen-bold-duotone" class="tw-text-lg" />
                                            </a-button>
                                            <button
                                                @click="deleteService(service)"
                                                class="tw-text-gray-400 hover:tw-text-red-600"
                                                title="Delete"
                                            >
                                                <icon icon="solar:trash-bin-2-bold-duotone" class="tw-text-lg" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="totalPages > 1" class="tw-flex tw-items-center tw-justify-between tw-mt-6">
                        <div class="tw-text-sm tw-text-gray-600">
                            Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} to {{ Math.min(currentPage * itemsPerPage, totalItems) }} of {{ totalItems }} services
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <a-button
                                @click="currentPage--"
                                :disabled="currentPage === 1 || loading"
                                intent="light"
                                left-icon="solar:arrow-left-linear"
                            >
                                Previous
                            </a-button>
                            <a-button
                                @click="currentPage++"
                                :disabled="currentPage === totalPages || loading"
                                intent="light"
                                right-icon="solar:arrow-right-linear"
                            >
                                Next
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <DeleteModal
            :visible="deleteModalVisible"
            :loading="deletingService"
            @confirm="confirmDelete"
            @cancel="deleteModalVisible = false"
            :title="deleteModalTitle"
            :description="deleteModalDescription"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import DeleteModal from '@/components/modals/DeleteModal.vue'
import FormField from '@/components/form/FormField.vue'
import useAlert from '@/composables/useAlert'
// API imports
import { paginate as paginateServices, destroy as destroyService } from '@/api/services'

const router = useRouter()
router
// Data
const loading = ref(false)
const searchQuery = ref('')
const viewMode = ref('grid')
const currentPage = ref(1)
const itemsPerPage = ref(12)
const selectedServices = ref([])
const deleteModalVisible = ref(false)
const deletingService = ref(false)
const serviceToDelete = ref(null)

// Filters
const filters = ref({
    status: 'all'
})

// API Response data
const services = ref([])
const totalItems = ref(0)
const totalPages = ref(0)
const lastPage = ref(1)

// Debounce search
let searchTimeout = null

// Computed properties
const allServicesSelected = computed(() => {
    return services.value.length > 0 && 
           services.value.every(service => selectedServices.value.includes(service.id))
})

const deleteModalTitle = computed(() => {
    if (selectedServices.value.length > 1) {
        return `Delete ${selectedServices.value.length} services?`
    }
    return 'Delete service?'
})

const deleteModalDescription = computed(() => {
    if (selectedServices.value.length > 1) {
        return 'This action cannot be undone. All selected services will be permanently deleted.'
    }
    return 'This action cannot be undone. The service will be permanently deleted.'
})



// Watchers
watch(searchQuery, () => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        currentPage.value = 1
        fetchServices()
    }, 500)
})

watch(currentPage, () => {
    fetchServices()
})

watch(() => filters.value.status, () => {
    currentPage.value = 1
    fetchServices()
})

// API Methods
const fetchServices = async () => {
    loading.value = true

    try {
        const params = {
            page: currentPage.value,
            per_page: itemsPerPage.value,
            search: searchQuery.value || undefined,
            status: filters.value.status !== 'all' ? filters.value.status : undefined
        }

        const response = await paginateServices(params)
        
        if (response.data) {
            services.value = response.data.data || []
            totalItems.value = response.data.total || 0
            totalPages.value = response.data.last_page || 1
            lastPage.value = response.data.last_page || 1
            
            // Debug logging
            console.log('API Response:', response.data)
            console.log('Services loaded:', services.value.length)
        }
    } catch (error) {
        console.error('Error fetching services:', error)
        services.value = []
        totalItems.value = 0
        totalPages.value = 1
        
        // Handle different error types
        if (error.response?.status === 401) {
            useAlert('Authentication required. Please log in again.', 'danger')
        } else if (error.response?.status >= 500) {
            useAlert('Server error. Please try again later.', 'danger')
        } else {
            useAlert('Failed to load services. Please check your connection.', 'warning')
        }
    } finally {
        loading.value = false
    }
}

// Methods
const onFilterChange = () => {
    currentPage.value = 1
    fetchServices()
}

const clearFilters = () => {
    searchQuery.value = ''
    filters.value.status = 'all'
    currentPage.value = 1
    fetchServices()
}

const toggleServiceSelection = (serviceId) => {
    const index = selectedServices.value.indexOf(serviceId)
    if (index > -1) {
        selectedServices.value.splice(index, 1)
    } else {
        selectedServices.value.push(serviceId)
    }
}

const toggleAllServices = () => {
    if (allServicesSelected.value) {
        selectedServices.value = []
    } else {
        selectedServices.value = services.value.map(service => service.id)
    }
}

const selectService = (service) => {
    viewService(service)
}

const viewService = (service) => {
    console.log('Viewing service:', service.name)
    // In a real app, this would open a preview modal or navigate to service detail
}

const deleteService = (service) => {
    serviceToDelete.value = service
    selectedServices.value = [service.id]
    deleteModalVisible.value = true
}

const confirmDelete = async () => {
    deletingService.value = true
    
    try {
        // Delete services one by one
        for (const serviceId of selectedServices.value) {
            await destroyService(serviceId)
        }
        
        // Refresh the list
        await fetchServices()
        
        // Reset selections
        selectedServices.value = []
        serviceToDelete.value = null
        deleteModalVisible.value = false
        
        useAlert('Service(s) deleted successfully', 'success')
    } catch (error) {
        console.error('Error deleting service:', error)
        
        if (error.response?.status === 401) {
            useAlert('Authentication required. Please log in again.', 'danger')
        } else if (error.response?.status === 403) {
            useAlert('You do not have permission to delete services.', 'danger')
        } else if (error.response?.status >= 500) {
            useAlert('Server error. Please try again later.', 'danger')
        } else {
            useAlert('Error deleting service(s). Please try again.', 'danger')
        }
    } finally {
        deletingService.value = false
    }
}

const formatDate = (date) => {
    if (!date) return '--'
    try {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        })
    } catch (error) {
        console.error('Error formatting date:', date, error)
        return '--'
    }
}

// Initialize
onMounted(() => {
    fetchServices()
})
</script>

<style scoped>
.tw-line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>